// Built-in Reminders app — personal, per-user reminders backed by the
// generic records plane (workspaceProject.records, collection: 'reminders').
// No dedicated server table: every row is { id, collection, name, data,
// created_at, updated_at } — `name` carries the title, everything else
// (done/dueAt/notes/ownerEmail) rides the `data` blob (PATCH shallow-merges
// `data`, so a toggle only ever sends `{ data: { done } }`).
//
// PERSONAL SCOPE (v1): the server does not yet scope /records by owner, so
// every function here filters/writes strictly through `mine()` — only rows
// whose `data.ownerEmail === root.currentUserEmail` are ever shown or
// mutated. Tightening this server-side is a known follow-up (see the
// workspace ticket board), not attempted here.
//
// Registered on the project via functions/index.js barrel so every export
// below resolves through `el.call('fnName', …)` (never imported directly —
// see components/ReminderRow.js, components/ReminderComposer.js,
// pages/reminders.js).

const COLLECTION = 'reminders'

const _logErr = (scope, err) => console.error(`[${scope}]`, (err && err.message) || err)

const _tempId = () => `temp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
const _isTempId = (id) => typeof id === 'string' && id.startsWith('temp-')
const _nowIso = () => new Date().toISOString()

// Workspace scope for every records call. The records plane routes by
// /workspaces/:id/ and the SDK throws `no workspace scope` when neither an
// explicit workspaceId nor an SDK-active workspace exists — which is exactly
// the org-home case (/w/<org> has no ws segment, so nothing ever set the
// SDK context). Resolution order mirrors the shell: the URL-resolved
// workspace doc, then the persisted active-workspace pointer, then the
// org's first workspace. Passed as a top-level routing param (params /
// payload-adjacent) — WS_CRUD_ARG_MAP threads it into the options
// positional, never the request body.
export const reminderScopeWs = function (root) {
  const r = root || (this && this.state && this.state.root) || null
  if (!r) return null
  const wsDoc = r.workspace && (r.workspace.id || r.workspace._id)
  if (wsDoc) return wsDoc
  if (r.activeWorkspaceId) return r.activeWorkspaceId
  const claims = r.jwtClaims || {}
  if (claims.active_workspace_id) return claims.active_workspace_id
  let pointer = null
  try {
    pointer = this && this.call ? this.call('getActiveWorkspacePointer') : null
  } catch (_) {
    pointer = null
  }
  if (pointer) return pointer
  const first = Array.isArray(r.workspaces) && r.workspaces[0]
  return (first && (first.id || first._id)) || null
}

// Only the signed-in user's own reminders — client-side personal-scope gate
// (see PERSONAL SCOPE note above).
const _mine = (root) => {
  const me = (root && root.currentUserEmail ? root.currentUserEmail : '').toLowerCase()
  if (!me) return []
  return ((root && root.reminders) || []).filter(
    (r) => ((r && r.ownerEmail) || '').toLowerCase() === me
  )
}

// Wire row → client shape. Always carries `key` (== id) so childrenAs:'state'
// lists reconcile by identity instead of array index (DOMQL keys off
// `rawChild.key` for childrenAs:'state' — see create.js child-key resolution).
export const rowToReminder = (row) => {
  const id = (row && (row.id || row._id)) || ''
  const data = (row && row.data) || {}
  return {
    id,
    key: id,
    title: (row && row.name) || '',
    done: !!data.done,
    dueAt: data.dueAt || null,
    notes: data.notes || '',
    ownerEmail: data.ownerEmail || '',
    // 'private' (owner-only, the default) | 'shared' (whole workspace).
    visibility: data.visibility === 'shared' ? 'shared' : 'private',
    // Emails @mentioned in the title at create time (already notified).
    mentions: Array.isArray(data.mentions) ? data.mentions : [],
    // Set by reminderDueSweep after the due-time notification fired — the
    // re-fire guard that survives reloads and other devices.
    notifiedAt: data.notifiedAt || null,
    createdAt: (row && row.created_at) || null,
    updatedAt: (row && row.updated_at) || null
  }
}

// Rows for the selected Files-style scope. Private means only the current
// member's private rows; Shared means workspace-visible rows. The scope switch
// is intentionally a view selector, not a transport parameter: the generic
// records plane returns the collection, then the app applies its own
// creator/visibility contract just as the Files canvas does.
const _shellVisible = (root) => {
  const me = (root && root.currentUserEmail ? root.currentUserEmail : '').toLowerCase()
  if (!me) return []
  const scope = root && root.remindersScope === 'shared' ? 'shared' : 'private'
  return ((root && root.reminders) || []).filter((r) => {
    if (!r) return false
    if (scope === 'shared') return r.visibility === 'shared'
    return r.visibility !== 'shared' && (r.ownerEmail || '').toLowerCase() === me
  })
}

export const reminderIsMine = function (root, r) {
  const rt = root || (this && this.state && this.state.root)
  const me = (rt && rt.currentUserEmail ? rt.currentUserEmail : '').toLowerCase()
  return !!me && ((r && r.ownerEmail) || '').toLowerCase() === me
}

// Parse @mentions out of a title against the people roster. Matches
// '@First', '@First Last' (greedy two-token try first) and '@email-local'
// case-insensitively; returns unique emails, never the author's own.
export const reminderParseMentions = function (root, title) {
  const rt = root || (this && this.state && this.state.root)
  const people = (rt && rt.people) || []
  const me = (rt && rt.currentUserEmail ? rt.currentUserEmail : '').toLowerCase()
  const found = new Set()
  const re = /@([\p{L}\p{N}._-]+(?:\s+[\p{L}\p{N}._-]+)?)/gu
  let m
  while ((m = re.exec(title || ''))) {
    const two = m[1].toLowerCase()
    const one = two.split(/\s+/)[0]
    for (const cand of [two, one]) {
      const hit = people.find((p) => {
        const name = (p.name || '').toLowerCase()
        const first = name.split(/\s+/)[0]
        const local = (p.email || '').split('@')[0].toLowerCase()
        return name === cand || first === cand || local === cand
      })
      if (hit && (hit.email || '').toLowerCase() !== me) {
        found.add(hit.email)
        break
      }
    }
  }
  return [...found]
}

// Optimistic create — a temp row renders immediately; the real id (and
// server timestamps) replace it on success. On failure the optimistic row
// is rolled back and a toast surfaces the error (mirrors calendar's
// saveEvent.js optimistic-id convention).
export const createReminder = async function (payload) {
  const root = this && this.state && this.state.root
  const fetch = this && this.context && this.context.fetch
  if (!root) return null
  const title = ((payload && payload.title) || '').trim()
  if (!title) return null
  const me = root.currentUserEmail || ''
  const tempId = _tempId()
  const now = _nowIso()
  const dueAt = (payload && payload.dueAt) || null
  const notes = (payload && payload.notes) || ''
  const visibility = (payload && payload.visibility) === 'shared' ? 'shared' : 'private'
  const mentions = reminderParseMentions.call(this, root, title)
  const optimistic = {
    id: tempId,
    key: tempId,
    title,
    done: false,
    dueAt,
    notes,
    ownerEmail: me,
    visibility,
    mentions,
    notifiedAt: null,
    createdAt: now,
    updatedAt: now
  }
  root.update({ reminders: [...(root.reminders || []), optimistic] })

  if (!fetch || !fetch.insert) {
    _logErr('createReminder', 'context.fetch.insert missing')
    return null
  }
  try {
    const wsId = reminderScopeWs.call(this, root)
    const result = await fetch.insert({
      from: 'workspaceProject.records',
      ...(wsId ? { params: { workspaceId: wsId } } : {}),
      data: {
        collection: COLLECTION,
        name: title,
        data: { done: false, dueAt, notes, ownerEmail: me, visibility, mentions }
      }
    })
    if (result && result.error) throw result.error
    const real = rowToReminder(result && result.data)
    if (!real.id) throw new Error('insert returned no id')
    root.update({
      reminders: (root.reminders || []).map((r) => (r.id === tempId ? real : r))
    })
    // Notify every @mentioned teammate (createNotification self-guards, so
    // the author can never notify themselves). Fire-and-forget — a failed
    // notification must not fail the create.
    for (const email of mentions) {
      try {
        this.call('createNotification', {
          userEmail: email,
          type: 'mention',
          title: this.call('polyglot', 'workspace.reminders.mentionTitle').replace('{name}', me),
          body: title + (notes ? ` — ${notes}` : ''),
          href: '/reminders'
        })
      } catch (_) {
        /* fire-and-forget */
      }
    }
    return real.id
  } catch (err) {
    _logErr('createReminder', err)
    // Roll back the optimistic row AND hand the typed text back to the
    // composer — losing the draft on a failed insert forces a retype.
    root.update({
      reminders: (root.reminders || []).filter((r) => r.id !== tempId),
      _reminderDraft: title,
      _reminderDraftDue: dueAt
    })
    this.call('openNotification', {
      title: this.call('polyglot', 'workspace.reminders.addFailed'),
      message: (err && err.message) || this.call('polyglot', 'workspace.reminders.tryAgain'),
      type: 'error'
    })
    return null
  }
}

// Optimistic toggle (done ⇄ open). Reverts + toasts on a failed PATCH.
export const toggleReminder = async function (id) {
  const root = this && this.state && this.state.root
  const fetch = this && this.context && this.context.fetch
  if (!root || !id) return
  const list = root.reminders || []
  const current = list.find((r) => r.id === id)
  if (!current) return
  const nextDone = !current.done
  const now = _nowIso()
  root.update({
    reminders: list.map((r) => (r.id === id ? { ...r, done: nextDone, updatedAt: now } : r))
  })
  if (_isTempId(id) || !fetch || !fetch.update) return
  try {
    const wsId = reminderScopeWs.call(this, root)
    const result = await fetch.update({
      from: 'workspaceProject.records',
      params: { id, ...(wsId ? { workspaceId: wsId } : {}) },
      data: { data: { done: nextDone } }
    })
    if (result && result.error) throw result.error
  } catch (err) {
    _logErr('toggleReminder', err)
    root.update({
      reminders: (root.reminders || []).map((r) =>
        r.id === id ? { ...r, done: current.done, updatedAt: current.updatedAt } : r
      )
    })
    this.call('openNotification', {
      title: this.call('polyglot', 'workspace.reminders.updateFailed'),
      message: (err && err.message) || this.call('polyglot', 'workspace.reminders.tryAgain'),
      type: 'error'
    })
  }
}

// Optimistic delete. On failure the row is restored (appended back — v1
// doesn't preserve its original position) and a toast surfaces the error.
export const deleteReminder = async function (id) {
  const root = this && this.state && this.state.root
  const fetch = this && this.context && this.context.fetch
  if (!root || !id) return
  const list = root.reminders || []
  const removed = list.find((r) => r.id === id)
  root.update({ reminders: list.filter((r) => r.id !== id) })
  if (_isTempId(id) || !fetch || !fetch.delete) return
  try {
    const wsId = reminderScopeWs.call(this, root)
    const result = await fetch.delete({
      from: 'workspaceProject.records',
      params: { id, ...(wsId ? { workspaceId: wsId } : {}) }
    })
    if (result && result.error) throw result.error
  } catch (err) {
    _logErr('deleteReminder', err)
    if (removed) root.update({ reminders: [...(root.reminders || []), removed] })
    this.call('openNotification', {
      title: this.call('polyglot', 'workspace.reminders.deleteFailed'),
      message: (err && err.message) || this.call('polyglot', 'workspace.reminders.tryAgain'),
      type: 'error'
    })
  }
}

// The ONE house confirm for a reminder delete (CRUD standard rule 3, D1): the
// row's trash icon and the ⋯ menu's "Delete reminder…" both open it — on
// origin/main c9d6dfbbd the icon deleted on one click while its menu twin
// asked. Owner-only, like the controls that open it: a teammate's shared
// reminder never reaches the write. The message says when the reminder is
// shared (it goes for everyone who sees it).
export const confirmDeleteReminder = function (id) {
  const root = this && this.state && this.state.root
  if (!root || !id) return
  const r = (root.reminders || []).find((x) => x && x.id === id)
  if (!r || !reminderIsMine(root, r)) return
  const name = String(r.title || '').trim()
  this.call('confirmDialog', {
    title: name
      ? this.call('polyglot', 'workspace.reminders.deleteTitle').replace('{name}', () => name)
      : this.call('polyglot', 'workspace.ctxMenu.reminder.deleteConfirm'),
    message: this.call(
      'polyglot',
      r.visibility === 'shared'
        ? 'workspace.reminders.deleteSharedBody'
        : 'workspace.ctxMenu.reminder.deleteBody'
    ),
    confirmText: this.call('polyglot', 'workspace.ctxMenu.reminder.deleteCta'),
    cancelText: this.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    // `null` is the explicit opt-out; OMITTING the key takes the template's
    // 'warning' default.
    icon: null,
    onConfirm: () => this.call('deleteReminder', id)
  })
}

// The ONE ask before "Convert to task" (row icon and ⋯ menu alike): the
// convert MOVES the item — it makes a ticket and deletes the reminder — so it
// is owner-only too (on origin/main the row ran it on one click, for anyone
// who could see the row).
export const confirmConvertReminderToTask = function (id) {
  const root = this && this.state && this.state.root
  if (!root || !id) return
  const r = (root.reminders || []).find((x) => x && x.id === id)
  if (!r || !reminderIsMine(root, r)) return
  this.call('confirmDialog', {
    title: this.call('polyglot', 'workspace.ctxMenu.reminder.toTicketConfirm'),
    message: this.call('polyglot', 'workspace.ctxMenu.reminder.toTicketBody'),
    confirmText: this.call('polyglot', 'workspace.ctxMenu.reminder.toTicketCta'),
    cancelText: this.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    icon: null,
    onConfirm: () => this.call('convertReminderToTask', id)
  })
}

const _FILTERS = new Set(['all', 'today', 'upcoming', 'completed'])
export const setRemindersFilter = function (key) {
  const root = this && this.state && this.state.root
  if (!root) return
  root.update({ _remindersFilter: _FILTERS.has(key) ? key : 'all' })
}

const _startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const _dayDiff = (dueAt) => {
  const d = new Date(dueAt)
  if (Number.isNaN(d.getTime())) return null
  return Math.round((_startOfDay(d) - _startOfDay(new Date())) / 86400000)
}

// Relative due-date chip label: 'Today 15:00' / 'Tomorrow' / 'Mon, Aug 4' /
// 'Overdue · 2d'. Only "Today" carries a time — the others are day-grained.
// `this` is the element (every caller is `el.call('reminderDueLabel', …)`).
export const reminderDueLabel = function (dueAt) {
  if (!dueAt) return null
  const d = new Date(dueAt)
  if (Number.isNaN(d.getTime())) return null
  const diff = _dayDiff(dueAt)
  if (diff == null) return null
  if (diff < 0)
    return this.call('polyglot', 'workspace.reminders.due.overdue').replace(
      '{count}',
      Math.abs(diff)
    )
  if (diff === 0) {
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return this.call('polyglot', 'workspace.reminders.due.today').replace('{time}', `${hh}:${mm}`)
  }
  if (diff === 1) return this.call('polyglot', 'workspace.reminders.due.tomorrow')
  // The SESSION language (shared localizedDate); this was pinned to en-US, so a
  // Georgian reminder read `Wed, Aug 19`.
  const out = this.call('localizedDate', d, { weekday: 'short', month: 'short', day: 'numeric' })
  return typeof out === 'string' && out
    ? out
    : `${d.toLocaleDateString('en-US', { weekday: 'short' })}, ${d.toLocaleDateString('en-US', { month: 'short' })} ${d.getDate()}`
}

export const reminderIsOverdue = (dueAt, done) => {
  if (done || !dueAt) return false
  const diff = _dayDiff(dueAt)
  return diff != null && diff < 0
}

const _sortByDue = (a, b) => {
  if (!a.dueAt && !b.dueAt) return (a.title || '').localeCompare(b.title || '')
  if (!a.dueAt) return 1
  if (!b.dueAt) return -1
  return new Date(a.dueAt) - new Date(b.dueAt)
}

// Grouped view model for the page body: Overdue / Today / Tomorrow /
// Upcoming / No date / Completed — only non-empty groups, Completed always
// last. `root._remindersFilter` narrows which groups are returned (the
// sidebar's All/Today/Upcoming/Completed rail).
export const reminderGroups = (root) => {
  const all = _shellVisible(root)
  const open = all.filter((r) => !r.done)
  const done = all.filter((r) => r.done)
  const filter = (root && root._remindersFilter) || 'all'

  const overdue = []
  const today = []
  const tomorrow = []
  const upcoming = []
  const noDate = []
  for (const r of open) {
    const diff = r.dueAt ? _dayDiff(r.dueAt) : null
    if (diff == null) noDate.push(r)
    else if (diff < 0) overdue.push(r)
    else if (diff === 0) today.push(r)
    else if (diff === 1) tomorrow.push(r)
    else upcoming.push(r)
  }
  ;[overdue, today, tomorrow, upcoming].forEach((arr) => arr.sort(_sortByDue))
  noDate.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
  done.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))

  const ALL_GROUPS = [
    { key: 'overdue', label: 'workspace.reminders.group.overdue', tone: 'error', items: overdue },
    { key: 'today', label: 'workspace.reminders.group.today', items: today },
    { key: 'tomorrow', label: 'workspace.reminders.group.tomorrow', items: tomorrow },
    { key: 'upcoming', label: 'workspace.reminders.group.upcoming', items: upcoming },
    { key: 'nodate', label: 'workspace.reminders.group.noDate', items: noDate },
    { key: 'completed', label: 'workspace.reminders.group.completed', muted: true, items: done }
  ]

  let picked
  if (filter === 'today')
    picked = ALL_GROUPS.filter((g) => g.key === 'overdue' || g.key === 'today')
  else if (filter === 'upcoming')
    picked = ALL_GROUPS.filter((g) => g.key === 'tomorrow' || g.key === 'upcoming')
  else if (filter === 'completed') picked = ALL_GROUPS.filter((g) => g.key === 'completed')
  else picked = ALL_GROUPS

  return picked.filter((g) => g.items.length)
}

// Live counts for the sidebar filter chips. "Today" folds overdue in (an
// overdue reminder still needs attention today) — mirrors reminderGroups'
// 'today' filter picking both the overdue + today buckets.
//
// WAVE-B: `all` must count everything the 'all' filter's ALL_GROUPS actually
// renders in reminderGroups — which includes the trailing `completed` group
// (picked = ALL_GROUPS, unfiltered, when filter is 'all'/unset). Counting
// only `open.length` here disagreed with that view: the sidebar badge read 0
// while the "All" tab was actively rendering completed reminders.
export const reminderCounts = (root) => {
  const all = _shellVisible(root)
  const open = all.filter((r) => !r.done)
  const done = all.filter((r) => r.done)
  let today = 0
  let upcoming = 0
  for (const r of open) {
    if (!r.dueAt) continue
    const diff = _dayDiff(r.dueAt)
    if (diff == null) continue
    if (diff <= 0) today += 1
    else upcoming += 1
  }
  return { all: all.length, today, upcoming, completed: done.length }
}

// Files-style scope setter: switching planes leaves the fetched cache intact
// and only re-renders the appropriate rows. Align the composer’s visibility
// default at the same time so a new reminder lands in the visible plane.
export const setRemindersScope = function (scope) {
  const root = this && this.state && this.state.root
  if (!root) return
  const next = scope === 'shared' ? 'shared' : 'private'
  if (root.remindersScope === next && !!root._reminderDraftShared === (next === 'shared')) return
  root.update({
    remindersScope: next,
    _reminderDraftShared: next === 'shared'
  })
}

// Empty-state copy per active filter (task design bar — one-liner + icon).
export const reminderEmptyText = function (root) {
  const t = (k) => this.call('polyglot', k)
  if (root && root._remindersLoadError) return t('workspace.reminders.empty.notEnabled')
  const filter = (root && root._remindersFilter) || 'all'
  if (filter === 'today') return t('workspace.reminders.empty.today')
  if (filter === 'upcoming') return t('workspace.reminders.empty.upcoming')
  if (filter === 'completed') return t('workspace.reminders.empty.completed')
  return t(
    root && root.remindersScope === 'shared'
      ? 'workspace.reminders.empty.shared'
      : 'workspace.reminders.empty.private'
  )
}

// Quick-chip due values (Today / Tomorrow / Next week), all defaulted to a
// 9am local reminder time — a plain, predictable v1 default.
const _atNineAm = (date) => {
  const d = new Date(date)
  d.setHours(9, 0, 0, 0)
  return d
}
export const reminderQuickDue = (kind) => {
  const now = new Date()
  if (kind === 'today') return _atNineAm(now).toISOString()
  if (kind === 'tomorrow') {
    const d = new Date(now)
    d.setDate(d.getDate() + 1)
    return _atNineAm(d).toISOString()
  }
  if (kind === 'nextWeek') {
    const d = new Date(now)
    d.setDate(d.getDate() + 7)
    return _atNineAm(d).toISOString()
  }
  return null
}

// ISO (stored) ⇄ `datetime-local` input value (`YYYY-MM-DDTHH:mm`, local
// wall-clock, no offset) conversions for the composer's "Pick…" field.
export const reminderDraftDueInputValue = (root) => {
  const iso = root && root._reminderDraftDue
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`
}

export const setReminderDraftDue = function (localValue) {
  const root = this && this.state && this.state.root
  if (!root) return
  if (!localValue) {
    root.update({ _reminderDraftDue: null })
    return
  }
  const d = new Date(localValue)
  if (Number.isNaN(d.getTime())) return
  root.update({ _reminderDraftDue: d.toISOString() })
}

export const clearReminderDraftDue = function () {
  const root = this && this.state && this.state.root
  if (!root) return
  root.update({ _reminderDraftDue: null })
}

// Shared submit path for both the composer's Enter key and its Add button —
// one code path so the two triggers can never drift.
export const submitReminderDraft = async function () {
  const root = this && this.state && this.state.root
  if (!root) return
  const title = (root._reminderDraft || '').trim()
  if (!title) return
  const dueAt = root._reminderDraftDue || null
  const visibility = root._reminderDraftShared ? 'shared' : 'private'
  root.update({
    _reminderDraft: '',
    _reminderDraftDue: null,
    _reminderDraftShared: false,
    _reminderMentionQuery: null
  })
  await this.call('createReminder', { title, dueAt, visibility })
}

// ── Composer @mention autocomplete glue ─────────────────────────────────
// The input's onInput calls this with the live value: a trailing '@token'
// arms the suggestion menu (root._reminderMentionQuery), anything else
// disarms it.
export const setReminderMentionQuery = function (value) {
  const root = this && this.state && this.state.root
  if (!root) return
  const m = /@([\p{L}\p{N}._-]*)$/u.exec(value || '')
  const next = m ? m[1].toLowerCase() : null
  if ((root._reminderMentionQuery || null) !== next) root.update({ _reminderMentionQuery: next })
}

// People matching the armed query (root._reminderMentionQuery), capped for
// the dropdown. Empty query (bare '@') lists the first few teammates.
export const reminderMentionMatches = function (root) {
  const rt = root || (this && this.state && this.state.root)
  const q = rt && rt._reminderMentionQuery
  if (q == null) return []
  const me = (rt.currentUserEmail || '').toLowerCase()
  return ((rt && rt.people) || [])
    .filter((p) => (p.email || '').toLowerCase() !== me)
    .filter((p) => {
      if (!q) return true
      const name = (p.name || '').toLowerCase()
      const local = (p.email || '').split('@')[0].toLowerCase()
      return name.includes(q) || local.includes(q)
    })
    .slice(0, 6)
}

// Replace the armed trailing '@token' in the draft with '@FirstName ' and
// disarm the menu.
export const applyReminderMention = function (person) {
  const root = this && this.state && this.state.root
  if (!root || !person) return
  const first = ((person.name || person.email || '').split(/\s+/)[0] || '').trim()
  const draft = root._reminderDraft || ''
  root.update({
    _reminderDraft: draft.replace(/@([\p{L}\p{N}._-]*)$/u, `@${first} `),
    _reminderMentionQuery: null
  })
}

export const toggleReminderDraftShared = function () {
  const root = this && this.state && this.state.root
  if (!root) return
  const next = !root._reminderDraftShared
  root.update({
    _reminderDraftShared: next,
    remindersScope: next ? 'shared' : 'private'
  })
}

// ── Convert actions ─────────────────────────────────────────────────────
// Reminder → ticket: creates a backlog ticket with the reminder's title +
// notes, then deletes the reminder (a convert MOVES the item). saveTicket
// is the tickets package's canonical writer (registered globally via the
// eager slice).
export const convertReminderToTask = async function (id) {
  const root = this && this.state && this.state.root
  if (!root || !id) return
  const r = (root.reminders || []).find((x) => x.id === id)
  // Owner-only (it deletes the reminder): the same rule as the delete.
  if (!r || !reminderIsMine(root, r)) return
  try {
    const created = await this.call('saveTicket', {
      title: r.title,
      description: r.notes || '',
      columnKey: 'backlog'
    })
    if (!created) throw new Error('ticket create returned nothing')
    await this.call('deleteReminder', id)
    this.call('openNotification', {
      type: 'success',
      title: this.call('polyglot', 'workspace.reminders.converted'),
      message: r.title,
      href: '/tasks'
    })
  } catch (err) {
    _logErr('convertReminderToTask', err)
    this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'workspace.reminders.convertFailed'),
      message: (err && err.message) || this.call('polyglot', 'workspace.reminders.tryAgain')
    })
  }
}

// Reminder → calendar event: opens the global New Booking modal prefilled
// from the reminder (date/time from dueAt when set). The reminder stays —
// the user may cancel the modal; delete it manually once booked.
export const convertReminderToEvent = function (id) {
  const root = this && this.state && this.state.root
  if (!root || !id) return
  const r = (root.reminders || []).find((x) => x.id === id)
  if (!r) return
  const overrides = { title: r.title, description: r.notes || '' }
  if (r.dueAt) {
    const d = new Date(r.dueAt)
    if (!Number.isNaN(d.getTime())) {
      const pad = (n) => String(n).padStart(2, '0')
      overrides.date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      overrides.startTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`
      const end = new Date(d.getTime() + 3600000)
      overrides.endTime = `${pad(end.getHours())}:${pad(end.getMinutes())}`
    }
  }
  this.call('openNewBookingModal', overrides)
}

// ── Due-time notifications ──────────────────────────────────────────────
// Sweeps MY open reminders whose dueAt has passed and that haven't fired
// yet (notifiedAt guard persists via the records PATCH so other devices /
// later sessions don't re-fire). Local toast + chime immediately; a bell
// row is written through the notifications entity so the center shows it
// too. Throttled internally so callers can invoke it freely.
// WS-SWITCH-MEMO-PLANE-1: the throttle is TENANT-KEYED, not a single scalar.
// `_mine(root)` reads `root.reminders`, which the workspace switch wipes and
// refetches — but the throttle was one module-level number, so the sweep's
// "already ran" verdict survived the switch. Land inside the window and
// workspace B's already-overdue reminders fired NO toast, NO chime and NO bell
// row until the stamp aged out. Keying by `${currentUserEmail}|${wsId}` makes
// the new tenant a natural MISS — no teardown call to remember, the pattern
// `_dashCache` (analytics) and `NOTIF_SCOPE` (notifications) already use here.
// Bounded so a long session across many workspaces cannot grow it without end.
const _dueSweepLast = new Map()
const _DUE_SWEEP_MS = 55000
const _DUE_SWEEP_MAX_SCOPES = 50
const _dueSweepScope = function (root) {
  const me = (root && root.currentUserEmail) || ''
  let wsId = null
  try {
    wsId = reminderScopeWs.call(this, root)
  } catch (_) {
    wsId = null
  }
  // An unresolved workspace is a DIFFERENT scope, never a shared one — the
  // '' / 'anon' fallback shape this ticket names as the one way these regress.
  return `${me}|${wsId == null ? '' : String(wsId)}`
}
export const reminderDueSweep = async function () {
  const root = this && this.state && this.state.root
  const fetch = this && this.context && this.context.fetch
  if (!root || !fetch || !fetch.update) return
  const nowMs = Date.now()
  const scope = _dueSweepScope.call(this, root)
  const last = _dueSweepLast.get(scope) || 0
  if (nowMs - last < _DUE_SWEEP_MS) return
  if (_dueSweepLast.size >= _DUE_SWEEP_MAX_SCOPES && !_dueSweepLast.has(scope)) {
    _dueSweepLast.delete(_dueSweepLast.keys().next().value)
  }
  _dueSweepLast.set(scope, nowMs)
  const me = root.currentUserEmail || ''
  if (!me) return
  const due = _mine(root).filter(
    (r) =>
      !r.done &&
      r.dueAt &&
      new Date(r.dueAt).getTime() <= nowMs &&
      (!r.notifiedAt || new Date(r.notifiedAt) < new Date(r.dueAt))
  )
  for (const r of due) {
    const stamp = _nowIso()
    // Optimistic local guard first so a slow PATCH can't double-fire within
    // this session.
    root.update({
      reminders: (root.reminders || []).map((x) =>
        x.id === r.id ? { ...x, notifiedAt: stamp } : x
      )
    })
    this.call('openNotification', {
      type: 'info',
      title: this.call('polyglot', 'workspace.reminders.due'),
      message: r.title + (r.notes ? ` — ${r.notes}` : ''),
      icon: 'bell',
      sound: true,
      href: '/reminders'
    })
    try {
      const wsId = reminderScopeWs.call(this, root)
      await fetch.update({
        from: 'workspaceProject.records',
        params: { id: r.id, ...(wsId ? { workspaceId: wsId } : {}) },
        data: { data: { notifiedAt: stamp } }
      })
    } catch (err) {
      _logErr('reminderDueSweep', err)
    }
    // Bell-center row (self-targeted, so the guarded createNotification
    // shared fn can't be used — write the entity directly, fire-and-forget).
    try {
      const sdk = this.sdk || (this.context && this.context.sdk)
      if (sdk && typeof sdk.execute === 'function') {
        await sdk.execute('workspaceProject.notifications', 'create', {
          payload: {
            user_email: me,
            type: 'reminder',
            title: this.call('polyglot', 'workspace.reminders.due'),
            body: r.title,
            href: '/reminders'
          }
        })
      }
    } catch (_) {
      /* fire-and-forget */
    }
  }
}
